import { useState, useRef, useEffect } from 'react';
import { NumericTextBoxPropsContext, InputPropsContext } from '@progress/kendo-react-inputs';
import { DatePickerPropsContext } from '@progress/kendo-react-dateinputs';
import { Grid, GridColumn as Column, GridToolbar, GridNoRecords } from '@progress/kendo-react-grid'; 
import { process } from '@progress/kendo-data-query';
import { TableToolbar } from './TableToolbar';
import {
  // Column props helper
  getColumnProps,
  // Utility functions
  formatKeyToTitle,
  UniversalHeaderCell,
  RowExpansionCellElement,
  numericTextBoxPropsCallback,
  inputPropsCallback,
  datePickerPropsCallback,
  // Custom hooks
  useMenuIconTitlesEffect,
  useFilterInputPlaceholderEffect,
  useTableHeaderTitlesEffect,
  useSaveSelectionEffect,
  useDefaultSelectEffect,
  // Data filtering functions
  getSearchFilter,
  getCombinedFilter,
  // Table event handlers
  handleSearchChange,
  handleRenderedDataChange,
  handleFilterChange,
  handleGroupChange,
  handleSortChange,
  handleSelectionChange,
  handleExpandChange,
  handleExport
} from './helpers';
import { ExcelExport } from '../ExcelExport';

/**
 * Reusable table component for displaying data in a grid format.
 *
 * @component
 * @param {Object} props - component props.
 * @param {Array<Object>} [props.data=[]] - data to display in table.
 * @param {JSX.Element|null} [props.noRecords=null] - custom no records component or string (defaults to null). Used to replace 'No records available' message returned by Kendo.
 * @param {string} [props.title='Table'] - table title.
 * @param {Array<Object>} [props.columnConfig=[]] - column configuration array.
 * @param {Array<Object>} [props.headerCells=[]] - optional custom header cell configuration.
 * @param {string} [props.tableHeight='500px'] - height of table.
 * @param {boolean} [props.pageable=true] - whether table is pageable.
 * @param {number} [props.pageSize=10] - number of rows per page.
 * @param {Array<number>} [props.pageSizes=[5, 10, 20]] - available page sizes.
 * @param {string} [props.headerClassName=''] - additional CSS class for header cells.
 * @param {function} [props.onExportExcel] - callback for exporting to Excel (if enabled).
 * @param {boolean} [props.groupable=false] - whether column grouping is enabled.
 * @param {boolean} [props.filterable=false] - whether filtering is enabled.
 * @param {string} [props.scrollable='scrollable'] - whether vertical scrolling is enabled. ('scrollable', 'none', 'virtual')
 * @param {boolean} [props.selectable=false] - whether row selection is enabled.
 * @param {Object} [props.defaultSelect={}] - default selected rows (object with keys as dataItemKey and values as true).
 * @param {function} [props.handleSaveSelection] - callback for saving selected rows.
 * @param {boolean} [props.showSaveSelectionButton=false] - whether to show the Save Selection button in the toolbar.
 * @param {React.Ref} [props.excelExportRef] - ref for Excel export functionality.
 * @param {function} [props.detail] - function to render detail row content.
 * @param {boolean} [props.exportable] - whether export functionality is enabled.
 * @param {boolean} [props.resizable=false] - whether columns are resizable.
 * @param {boolean} [props.sortable=null] - whether sorting is enabled (null for default behavior).
 * @param {Array<Object>} [props.defaultSort=[]] - default sorting configuration.
 * @param {string} [props.dataItemKey=''] - unique key for data items (used for row expansion).
 * @param {boolean} [props.enableRowExpansion=false] - whether row expansion is enabled.
 * @param {Array<Object>} [props.excelExportData=null] - data to export
 * @param {Array<Object>} [props.excelExportColumns=null] - columns to export in Excel.
 * @param {string} [props.excelFileName=null] - custom file name for Excel export.
 * @param {Object} [props.excelExportRef] - ref for Excel export functionality.
 * @param {number} [props.buttonCount=10] - number of page buttons to display.
 * @param {boolean} [props.showSearchbox=false] - whether to use search box functionality.
 * @param {boolean} [props.showColumnChooser=false] - whether to use column chooser functionality.
 * @param {string} [props.containerMinWidth='360px'] - minimum width for the table container.
 * @param {function} [props.onRowClick=null] - callback for row click events.
 * @returns {JSX.Element} rendered table component.
 * 
 * @see {@link https://www.telerik.com/kendo-react-ui/components/grid | KendoReact Grid Documentation}
 * @see {@link https://www.telerik.com/kendo-react-ui/components/grid/columns | KendoReact Grid Columns Documentation}
 * @see {@link https://www.telerik.com/kendo-react-ui/components/free#react-data-grid | KendoReact Free Grid Documentation}
 * 
 * @description Requires config file (see TABLE.md).
 */
export const Table = ({
  data = [],
  noRecords = null,
  title = '',
  columnConfig = [],
  headerCells = [],
  tableHeight = '500px',
  pageable = true,
  pageSize = 10,
  pageSizes = [5, 10, 15, 20],
  buttonCount = 10,
  headerClassName = '',
  exportable,
  onExportExcel,
  excelExportRef,
  groupable = false,
  filterable = false,
  scrollable = 'scrollable',
  selectable = false,
  defaultSelect = {},
  handleSaveSelection = null,
  onTableViewChange,
  showSaveSelectionButton = false,
  resizable = false,
  detail,
  sortable = null,
  defaultSort = [],
  dataItemKey = '',
  enableRowExpansion = false,
  excelExportData = null,
  excelExportColumns = null,
  excelFileName = null, 
  showSearchbox = false, 
  showColumnChooser = false,
  containerMinWidth = '100px',
  onRowClick = null 
}) => {
  const [filter, setFilter] = useState(null);
  const [group, setGroup] = useState([]);
  const [sort, setSort] = useState(defaultSort);
  const [detailExpanded, setDetailExpanded] = useState({});
  const [searchValue, setSearchValue] = useState('');
  const [select, setSelect] = useState(defaultSelect);
  const [dataState, setDataState] = useState({
    take: pageSize,
    skip: 0
  });
  const gridRef = useRef(null);
  // Conditional paging: disable paging when grouping is active
  const isGrouping = group && group.length > 0;

  //Set up search filters
  const searchFilter = getSearchFilter(data, searchValue, showSearchbox);
  const combinedFilter = getCombinedFilter(filter, searchFilter);

  const result = process(data, {
    ...(!isGrouping ? dataState : {}),
    filter: combinedFilter,
    group,
    sort,
    select: selectable ? select : undefined
  });

  const processedData = result.data;
  // Update pageSize, pageSizes, and total while grouping
  const effectivePageSize = isGrouping ? data.length : pageSize;
  const effectivePageSizes = isGrouping ? undefined : pageSizes;

  // Adds titles to menu icons
  useMenuIconTitlesEffect(gridRef, processedData);
  // Adds titles to header cells
  useTableHeaderTitlesEffect(gridRef, processedData);
  // Sets the placeholder for filter inputs
  useFilterInputPlaceholderEffect();
  // Calls handleSaveSelection when select changes, if not using a save button
  useSaveSelectionEffect({
    selectable, 
    select, 
    handleSaveSelection, 
    data, 
    dataItemKey, 
    showSaveSelectionButton, 
    defaultSelect
  });
  // Syncs the select state with defaultSelect prop changes
  useDefaultSelectEffect(selectable, defaultSelect, select, setSelect);

  // Helper to sanitize and ensure .xlsx extension
  const setExcelFileName = (name) => {
    if (!name || typeof name !== 'string') return 'Export.xlsx';
    let fileName = name;
    if (!fileName.toLowerCase().endsWith('.xlsx')) {
      fileName += '.xlsx';
    }
    return fileName;
  };
  const fullExportDataRef = useRef(null);
  const fullExportData = process(data, {
    filter: combinedFilter,
    group,
    sort
  }).data;

  const ClickableRow = (props) => {
    const { ...trProps } = props.trProps || {};
    const clickable = !!onRowClick;
    return (
      <tr
        {...trProps}
        style={clickable ? { cursor: 'pointer' } : undefined}
      >
        {props.children}
      </tr>
    );
  };

  const columnsWithWidthCount = columnConfig.filter(col => col.width != null).length;
  const shouldSetDefaultWidth = columnsWithWidthCount > 0 && columnsWithWidthCount < columnConfig.length;

  // Notify parent component of table view changes
  useEffect(() => {
    if (fullExportDataRef.current !== fullExportData) {
      fullExportDataRef.current = fullExportData;

      if (onTableViewChange) {
        onTableViewChange(fullExportData);
      }
    }
  }, [fullExportData]);

  return (
    <DatePickerPropsContext.Provider value={datePickerPropsCallback}>
     <NumericTextBoxPropsContext.Provider value={numericTextBoxPropsCallback}>
      <InputPropsContext.Provider value={inputPropsCallback}>
      <h2>{title}</h2>
      <div
        className="table-container"
        style={{
          width: '100%',
          maxHeight: tableHeight,
          overflow: 'auto',
          minWidth: containerMinWidth
        }}
        ref={gridRef}
      >
        <ExcelExport
          data={excelExportData ? excelExportData : fullExportData}
          fileName={setExcelFileName(excelFileName ? excelFileName : title)}
          ref={excelExportRef}
          columns={excelExportColumns ? excelExportColumns : columnConfig} // Optionally create specific columns for export (useful for detail components)
          group={group && group.length > 0 ? group : undefined}
        >
          <Grid
            data={processedData}
            total={result.total}
            onDataStateChange={e => handleRenderedDataChange(e, setDataState)}
            onRowClick={!selectable ? onRowClick : undefined}
            rows={{ data: (onRowClick && dataItemKey && !selectable) ? ClickableRow : undefined }}
            skip={dataState.skip}
            take={dataState.take}
            adaptive={true}
            style={{ maxHeight: tableHeight, height: isGrouping ? tableHeight : undefined }} // Set a table height while grouping for table to correctly calculate rows
            dataItemKey={dataItemKey}
            resizable={resizable}
            rowHeight={isGrouping ? 40 : undefined} // Set a row height while grouping for table to correctly calculate rows
            scrollable={isGrouping ? 'virtual' : scrollable} // virtual scrolling needed for large datasets while grouped on single page
            // filtering
            filterable={data.length > 0 ? filterable : false}
            filter={filter}
            onFilterChange={e => handleFilterChange(e, setFilter, setDataState)}
            // grouping
            groupable={data.length > 0 ? groupable : false}
            group={group}
            onGroupChange={e => handleGroupChange({event: e, setGroup, setDataState, pageSize, dataLength: data.length})}
            // paging - change pageSize/pageSizes while grouping
            pageable={pageable ? {buttonCount, pageSizes: effectivePageSizes } : undefined}
            pageSize={effectivePageSize}
            // sorting
            sort={sort}
            onSortChange={e => handleSortChange(e, setSort)}
            // selecting
            selectable={selectable ? { enabled: true, mode: 'multiple' } : false}
            select={select}
            {...(selectable && {
              onHeaderSelectionChange: e => handleSelectionChange(e, setSelect),
              onSelectionChange: e => handleSelectionChange(e, setSelect),
            })}
            // custom cells
            cells={{
              headerCell: (props) => {
                if (enableRowExpansion && !props.field) {
                  const pageRowIds = processedData.map(item => item[dataItemKey]) || [];
                  const anyPageRowsExpanded = pageRowIds.some(id => detailExpanded[id]) || false;

                  return (
                    <RowExpansionCellElement
                      headerProps={props}
                      hasExpandedRows={anyPageRowsExpanded}
                      onExpandAll={(e) => handleExpandChange({ event: e, resultData: processedData, dataItemKey, detailExpanded, setDetailExpanded })}
                    />
                  );
                }

                return UniversalHeaderCell(props, columnConfig, headerCells);
              }
            }}
            // If a detail row renderer is provided, enable Kendo Grid's detail row features:
            // - detail: function/component to render expanded row content
            // - onDetailExpandChange: callback to handle expand/collapse events
            // - detailExpand: object tracking expanded row state
            {...(detail && {
              detail: detail,
              onDetailExpandChange: e => handleExpandChange(e, processedData, dataItemKey, detailExpanded, setDetailExpanded),
              detailExpand: detailExpanded
            })}
          >
            {(exportable || showSearchbox || filterable || sortable || selectable || groupable) && data.length > 0 && ( // add more conditions here when needed with ORs (when more toolbar items become available)
              <GridToolbar>
                <TableToolbar
                  exportable={exportable}
                  onExport={() => handleExport(onExportExcel, excelExportRef)}
                  showSearchbox={showSearchbox}
                  searchValue={searchValue}
                  onSearchChange={e => handleSearchChange(e, setSearchValue, setDataState)}
                  filterable={filterable}
                  selectable={selectable}
                  groupable={groupable}
                  sortable={sortable}
                  onClearAllFilters={() => {
                    setFilter({ logic: 'and', filters: [] }); 
                    setSearchValue('');
                    setGroup([]);
                    setSort([]);
                    setDataState(prev => ({ ...prev, skip: 0, take: pageSize }));
                  }}
                  onSaveSelection={handleSaveSelection}
                  filter={filter}
                  select={selectable ? select : undefined}
                  defaultSelect={selectable ? defaultSelect : undefined}
                  showSaveSelectionButton={showSaveSelectionButton}
                  group={group}
                  sort={sort}
                  data={data}
                  dataItemKey={dataItemKey}
                  dataState={dataState}
                  pageSize={pageSize}
                />
              </GridToolbar>
            )} 
            {selectable && ( // Add checkbox column
              <Column minResizableWidth={40} filterable={false} columnType="checkbox" />
            )}
            {columnConfig
              && columnConfig.length > 0
              && columnConfig.map((item) => {
                // If only some columns have width, set a default width for those missing it
                const width = (shouldSetDefaultWidth && !item.width) ? 100 : item.width;

                // Grouped/parent column: renders a spanning header with nested sub-columns
                if (item.columns && item.columns.length > 0) {
                  return (
                    <Column key={item.field || item.title} title={item.title} headerClassName={item.headerClassName}>
                      {item.columns.map((subItem) => (
                        <Column
                          key={subItem.field}
                          minResizableWidth={subItem.minResizableWidth || 100}
                          field={subItem.field}
                          title={subItem.title || formatKeyToTitle(subItem.field)}
                          width={(shouldSetDefaultWidth && !subItem.width) ? 100 : subItem.width}
                          {...getColumnProps({
                              item: subItem,
                              filter,
                              sort,
                              group,
                              headerClassName,
                              sortable,
                              data,
                              showColumnChooser
                          })}
                        />
                      ))}
                    </Column>
                  );
                }

                return (
                  <Column
                    key={item.field || item.title}
                    minResizableWidth={item.minResizableWidth || 100}
                    field={item.field}
                    title={item.title || formatKeyToTitle(item.field)} // fallback if no header cell is provided
                    width={width}
                    {...getColumnProps({
                        item,
                        filter,
                        sort,
                        group,
                        headerClassName,
                        sortable,
                        data,
                        showColumnChooser
                    })}
                  />
                );
              })}
            {noRecords != null && noRecords !== false && (
              <GridNoRecords>{noRecords}</GridNoRecords>
            )}
          </Grid>
        </ExcelExport>
      </div>
      </InputPropsContext.Provider>
     </NumericTextBoxPropsContext.Provider>
    </DatePickerPropsContext.Provider>
  );
};